/**
 * Utilities for LTD aircraft ECD calculations.
 *
 * Rules:
 * - One crew per aircraft, 8 man-hours per business day (Mon–Fri)
 * - Parts are worked in earliest-EDD order
 * - Removal hours, when the part is still installed, start on the next open crew day
 * - Install hours start on the later of the next open crew time and the business day after EDD
 * - A part with availableOn is ready that day instead of the day after EDD
 * - Contiguous removal and install share the same 8-hour day
 */

export const normalizePartNo = (partNo) => {
    if (partNo == null) return '';
    return String(partNo).replace(/\s+/g, '').toUpperCase().trim();
};

export const manHoursToInstallDays = (manHours) => {
    const hours = Number(manHours);
    if (!Number.isFinite(hours) || hours <= 0) return 0;
    return Math.ceil(hours / 8);
};

const toDateOnly = (value) => {
    if (!value) return null;
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
        return new Date(value.getFullYear(), value.getMonth(), value.getDate());
    }
    const str = String(value).split('T')[0];
    const parts = str.split('-');
    if (parts.length !== 3) return null;
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1;
    const day = parseInt(parts[2], 10);
    const d = new Date(year, month, day);
    return Number.isNaN(d.getTime()) ? null : d;
};

export const isBusinessDay = (date) => {
    const day = date.getDay();
    return day !== 0 && day !== 6;
};

export const nextBusinessDayOnOrAfter = (date) => {
    const d = toDateOnly(date);
    if (!d) return null;
    while (!isBusinessDay(d)) {
        d.setDate(d.getDate() + 1);
    }
    return d;
};

/** Calendar day after EDD, adjusted to the next business day if needed. */
export const installStartAfterEdd = (edd) => {
    const base = toDateOnly(edd);
    if (!base) return null;
    base.setDate(base.getDate() + 1);
    return nextBusinessDayOnOrAfter(base);
};

/**
 * Add install business days where the start date counts as day 1.
 * Example: 8 hrs (1 day) starting Mon completes Mon.
 */
export const addBusinessDays = (startDate, installDays) => {
    const start = nextBusinessDayOnOrAfter(startDate);
    if (!start || installDays <= 0) return start;

    const result = new Date(start);
    let remaining = installDays - 1;
    while (remaining > 0) {
        result.setDate(result.getDate() + 1);
        if (isBusinessDay(result)) {
            remaining -= 1;
        }
    }
    return result;
};

export const nextBusinessDayAfter = (date) => {
    const d = toDateOnly(date);
    if (!d) return null;
    d.setDate(d.getDate() + 1);
    return nextBusinessDayOnOrAfter(d);
};

const HOURS_PER_DAY = 8;
const HUNDREDTHS_PER_DAY = HOURS_PER_DAY * 100;

const toHundredths = (hours) => {
    const value = Number(hours);
    if (!Number.isFinite(value) || value <= 0) return 0;
    return Math.round(value * 100);
};

const cloneDate = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

/**
 * Hours that will be scheduled for this part.
 * Removal is included only when CRD says the part is still installed.
 * Returns null when CRD or a required average is missing.
 */
const finiteHours = (value) => {
    if (value == null || value === '') return null;
    const hours = Number(value);
    return Number.isFinite(hours) ? hours : null;
};

/**
 * Install hours come only from the install average. A removal-only row stores
 * that average in avg_man_hours as well, and must not be treated as install time.
 */
export const referenceManHours = (row) => ({
    removeHours: finiteHours(row?.avg_remove_hours),
    installHours: finiteHours(row?.avg_install_hours),
});

export const plannedManHours = (part) => {
    if (part.crdStatus === 'installed') {
        if (part.removeHours == null || part.installHours == null) return null;
        return Number(part.removeHours) + Number(part.installHours);
    }
    if (part.crdStatus === 'not_installed') {
        if (part.installHours == null) return null;
        return Number(part.installHours);
    }
    return null;
};

const consumeHours = (cursor, hours) => {
    let remaining = toHundredths(hours);
    let date = cloneDate(cursor.date);
    let used = cursor.hoursUsedHundredths || 0;

    if (remaining === 0) {
        return {
            completion: cloneDate(date),
            cursor: { date, hoursUsedHundredths: used },
        };
    }

    while (remaining > 0) {
        const capacity = HUNDREDTHS_PER_DAY - used;
        if (remaining <= capacity) {
            used += remaining;
            remaining = 0;
            const completion = cloneDate(date);
            if (used >= HUNDREDTHS_PER_DAY) {
                return {
                    completion,
                    cursor: { date: nextBusinessDayAfter(date), hoursUsedHundredths: 0 },
                };
            }
            return {
                completion,
                cursor: { date, hoursUsedHundredths: used },
            };
        }
        remaining -= capacity;
        date = nextBusinessDayAfter(date);
        used = 0;
    }

    return {
        completion: cloneDate(date),
        cursor: { date, hoursUsedHundredths: used },
    };
};

const moveCursorTo = (cursor, target) => {
    if (!target) return cursor;
    if (cursor.date.getTime() < target.getTime()) {
        return { date: cloneDate(target), hoursUsedHundredths: 0 };
    }
    return cursor;
};

/** Business days after `earlier` up to and including `later`. Same day is 0. */
export const businessDaysBetween = (earlier, later) => {
    const start = toDateOnly(earlier);
    const end = toDateOnly(later);
    if (!start || !end) return null;
    if (end.getTime() <= start.getTime()) return 0;
    let count = 0;
    const cursor = cloneDate(start);
    while (cursor.getTime() < end.getTime()) {
        cursor.setDate(cursor.getDate() + 1);
        if (isBusinessDay(cursor)) count += 1;
    }
    return count;
};

/**
 * Compute sequential part ECDs and the aircraft final ECD.
 *
 * @param {Array<{
 *   partNo: string,
 *   edd: Date|null,
 *   removeHours: number|null,
 *   installHours: number|null,
 *   crdStatus: 'installed'|'not_installed'|'missing',
 *   availableOn?: Date|null,
 * }>} parts
 * @param {Date} [today=new Date()]
 * @returns {{parts: Array, finalEcd: Date|null, hasUnknownEcd: boolean}}
 */
export const computeSequentialEcd = (parts, today = new Date()) => {
    const todayDate = toDateOnly(today);
    let workCursor = {
        date: nextBusinessDayOnOrAfter(todayDate),
        hoursUsedHundredths: 0,
    };

    const dated = [];
    const undated = [];

    for (const part of parts) {
        const edd = toDateOnly(part.edd);
        const availableOn = toDateOnly(part.availableOn);
        const copy = { ...part, edd, availableOn };
        if (edd || availableOn) dated.push(copy);
        else undated.push(copy);
    }

    dated.sort((a, b) => {
        const aDate = a.edd || a.availableOn;
        const bDate = b.edd || b.availableOn;
        return aDate.getTime() - bDate.getTime();
    });
    const ordered = [...dated, ...undated];

    const results = [];
    let finalEcd = null;
    let hasUnknownEcd = false;

    for (const part of ordered) {
        const totalHours = plannedManHours(part);
        const workDays = totalHours == null ? null : manHoursToInstallDays(totalHours);
        let partEcd = null;

        const installGate = part.availableOn
            ? nextBusinessDayOnOrAfter(part.availableOn)
            : installStartAfterEdd(part.edd);
        if ((part.edd || part.availableOn) && installGate && totalHours != null && workCursor?.date) {
            let cursor = workCursor;
            if (part.crdStatus === 'installed' && Number(part.removeHours) > 0) {
                cursor = consumeHours(cursor, part.removeHours).cursor;
            }
            cursor = moveCursorTo(cursor, installGate);
            if (Number(part.installHours) > 0) {
                const installed = consumeHours(cursor, part.installHours);
                partEcd = installed.completion;
                cursor = installed.cursor;
            } else {
                partEcd = cloneDate(cursor.date);
            }
            workCursor = cursor;
            finalEcd = partEcd;
        } else {
            hasUnknownEcd = true;
        }

        results.push({
            ...part,
            totalHours,
            workDays,
            partEcd,
        });
    }

    if (results.some(r => !r.partEcd)) {
        hasUnknownEcd = true;
    }

    return { parts: results, finalEcd, hasUnknownEcd };
};

export const formatDate = (date) => {
    if (!date) return '';
    const d = toDateOnly(date);
    if (!d) return '';
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};
